import React, { useState, useEffect } from "react";
import {
  CTabs,
  CTabList,
  CTab,
  CTabContent,
  CTabPanel,
  CButton,
  CSpinner,
  CAlert,
} from "@coreui/react";
import ObjectDetails from "./Tabs/ObjectDetails";
import FilesTab from "./Tabs/FilesTab";
import EquipmentTab from "./Tabs/EquipmentTab";
import DefectsTab from "./Tabs/DefectsTab";
import ConstructionTab from "./Tabs/ConstructionTab";
import ProtocolsTab from "./Tabs/ProtocolsTab";
import NeuralModuleTab from "./Tabs/NeuralModuleTab";
import CIcon from "@coreui/icons-react";
import { cilCloudDownload, cilWarning } from "@coreui/icons";
import axios from "axios";

import "./InformationObject.scss";
import "../Button/Button.scss";
import "../Table/Table.scss";

const tabsConfig = [
  {
    key: 1,
    tabKey: "object_details",
    label: "Об объекте",
    component: ObjectDetails,
  },
  { key: 2, tabKey: "files", label: "Файлы", component: FilesTab },
  {
    key: 3,
    tabKey: "equipment",
    label: "Оборудование",
    component: EquipmentTab,
  },
  { key: 4, tabKey: "defects", label: "Дефекты", component: DefectsTab },
  {
    key: 5,
    tabKey: "construction",
    label: "Конструктив",
    component: ConstructionTab,
  },
  { key: 6, tabKey: "protocols", label: "Протоколы", component: ProtocolsTab },
  {
    key: 7,
    tabKey: "neural_module",
    label: "Нейромодуль",
    component: NeuralModuleTab,
  },
];

const InformationObject = ({ object }) => {
  const [activeTab, setActiveTab] = useState(1);
  const [userPermissions, setUserPermissions] = useState(null);
  const [allowedTabs, setAllowedTabs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const token = localStorage.getItem("token");

  useEffect(() => {
    const fetchUserPermissions = async () => {
      try {
        setLoading(true);
        setError(null);

        const response = await axios.get("/api/auth/permissions/", {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        setUserPermissions(response.data);
        const tabs = response.data.tabs?.allowed || [];
        setAllowedTabs(tabs);

        if (
          !response.data.quick_access?.is_approved &&
          !response.data.permissions?.is_admin
        ) {
        }
      } catch (error) {
        if (error.response?.status === 401) {
          setError(
            "Ошибка авторизации. Токен недействителен или истек срок действия. Пожалуйста, войдите снова.",
          );
        } else if (error.response?.status === 403) {
          setError(
            "Доступ запрещен. У вас нет прав для просмотра этой страницы.",
          );
        } else if (error.response?.status === 404) {
          setError("Endpoint не найден. Проверьте URL адрес.");
        } else {
          setError(
            "Не удалось получить права доступа. Проверьте подключение к серверу.",
          );
        }

        setAllowedTabs(tabsConfig.map((tab) => tab.tabKey));
      } finally {
        setLoading(false);
      }
    };

    if (token) {
      fetchUserPermissions();
    } else {
      setError("Токен авторизации не найден. Пожалуйста, войдите в систему.");
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    const handleGlobalKeyDown = (e) => {
      if (e.key === "Delete" || e.code === "Delete" || e.keyCode === 46) {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        return false;
      }
    };

    window.addEventListener("keydown", handleGlobalKeyDown, true);

    return () => {
      window.removeEventListener("keydown", handleGlobalKeyDown, true);
    };
  }, []);

  const isAndroidApp = !!window.Android;

  const postMessageToAndroid = (message) => {
    if (isAndroidApp && window.Android.postMessage) {
      window.Android.postMessage(JSON.stringify(message));
    }
  };

  const handleSyncObject = () => {
    if (object?.LDoc) {
      const message = {
        action: "sync",
        ldoc: object.LDoc,
        token: token,
      };
      postMessageToAndroid(message);
    }
  };

  const getFilteredTabs = () => {
    if (loading || error) {
      return tabsConfig;
    }

    if (!userPermissions || !allowedTabs) {
      return tabsConfig;
    }

    if (
      !userPermissions.quick_access?.is_approved &&
      !userPermissions.permissions?.is_admin
    ) {
      return [];
    }

    if (
      userPermissions.permissions?.is_admin ||
      userPermissions.tabs?.has_all
    ) {
      return tabsConfig;
    }

    return tabsConfig.filter((tab) => allowedTabs.includes(tab.tabKey));
  };

  const filteredTabs = getFilteredTabs();

  const getActiveTabKey = () => {
    const isActiveTabAvailable = filteredTabs.some(
      (tab) => tab.key === activeTab,
    );
    if (!isActiveTabAvailable && filteredTabs.length > 0) {
      return filteredTabs[0].key;
    }
    return activeTab;
  };

  if (
    !loading &&
    !error &&
    userPermissions &&
    !userPermissions.quick_access?.is_approved &&
    !userPermissions.permissions?.is_admin
  ) {
    return (
      <div className="information-object-container">
        <CAlert color="warning" className="d-flex align-items-center">
          <CIcon
            icon={cilWarning}
            className="flex-shrink-0 me-3"
            width={24}
            height={24}
          />
          <div>
            <h5>Доступ ограничен</h5>
            <p className="mb-0">
              Ваш аккаунт ожидает подтверждения администратором. Обратитесь к
              администратору для получения доступа к системе.
            </p>
          </div>
        </CAlert>
      </div>
    );
  }

  if (!loading && !error && filteredTabs.length === 0) {
    return (
      <div className="information-object-container">
        <CAlert color="danger" className="d-flex align-items-center">
          <CIcon
            icon={cilWarning}
            className="flex-shrink-0 me-3"
            width={24}
            height={24}
          />
          <div>
            <h5>Доступ запрещен</h5>
            <p className="mb-0">
              У вас нет доступа ни к одной из вкладок этого объекта. Обратитесь
              к администратору для получения необходимых прав.
            </p>
            {userPermissions && (
              <div className="mt-2 small">
                <span className="text-muted">
                  Доступно вкладок: {userPermissions.tabs?.allowed_count || 0}{" "}
                  из {userPermissions.tabs?.total_count || 7}
                </span>
              </div>
            )}
          </div>
        </CAlert>
      </div>
    );
  }

  if (loading) {
    return (
      <div
        className="information-object-container d-flex justify-content-center align-items-center"
        style={{ minHeight: "300px" }}
      >
        <div className="text-center">
          <CSpinner color="primary" />
          <p className="mt-3">Загрузка прав доступа...</p>
          <small className="text-muted">Проверка авторизации...</small>
        </div>
      </div>
    );
  }

  if (error && filteredTabs.length === 0) {
    return (
      <div className="information-object-container">
        <CAlert color="danger">
          <CIcon icon={cilWarning} className="me-2" />
          {error}
          <div className="mt-2 small">
            <button
              className="btn btn-sm btn-outline-primary"
              onClick={() => window.location.reload()}
            >
              Обновить страницу
            </button>
          </div>
        </CAlert>
      </div>
    );
  }

  const currentActiveTab = getActiveTabKey();

  return (
    <div className="information-object-container">
      {isAndroidApp && filteredTabs.length > 0 && (
        <div
          className="sync-button-container"
          style={{ padding: "10px", textAlign: "right" }}
        >
          <CButton color="primary" onClick={handleSyncObject}>
            <CIcon icon={cilCloudDownload} className="me-2" />
            Синхронизировать объект
          </CButton>
        </div>
      )}

      {error && filteredTabs.length > 0 && (
        <CAlert color="warning" className="mb-3">
          <CIcon icon={cilWarning} className="me-2" />
          {error} Вкладки отображены в полном доступе.
        </CAlert>
      )}

      <CTabs
        activeItemKey={currentActiveTab}
        onActiveItemChange={(key) => {
          setActiveTab(key);
        }}
      >
        <CTabList className="custom-tab-list" variant="tabs" layout="fill">
          {filteredTabs.map((tab) => (
            <CTab key={tab.key} itemKey={tab.key} className="custom-tab">
              {tab.label}
            </CTab>
          ))}
        </CTabList>
        <CTabContent>
          {filteredTabs.map((tab) => (
            <CTabPanel key={tab.key} itemKey={tab.key}>
              <tab.component object={object} activeTab={currentActiveTab} />
            </CTabPanel>
          ))}
        </CTabContent>
      </CTabs>
    </div>
  );
};

export default InformationObject;
